// Website reverse engineering (docs/reverse-engineering/PLAN.md) — shared, client-safe contract.
// A plan created from a URL carries one or more `site_analysis` rows; the newest one is the
// dissection the interview (grilling), the PRD and the Agent chat read. No server imports here.

export type SiteStatus = 'queued' | 'running' | 'done' | 'error'
export type SitePhase = 'queued' | 'robots' | 'discover' | 'fetch' | 'render' | 'analyze' | 'done' | 'error'
export const SITE_PHASES: readonly SitePhase[] = ['robots', 'discover', 'fetch', 'render', 'analyze', 'done']

export type PageKind =
  | 'home' | 'pricing' | 'features' | 'product' | 'solutions' | 'how' | 'about' | 'integrations'
  | 'customers' | 'faq' | 'docs' | 'blog' | 'contact' | 'signup' | 'login' | 'legal' | 'careers' | 'other'

/** One page Bystep read. Raw text is never sent to the browser; only this summary. */
export interface SitePage {
  url: string
  kind: PageKind
  title: string
  status: number
  /** visible words on the page (after rendering when the page needed it) */
  words: number
  /** read through the sandboxed browser (JavaScript page or screenshot) */
  rendered: boolean
  /** screenshot index for GET /api/plan/:id/site/shot?n= (null = none) */
  shot: number | null
  /** why a page was skipped or only partly read (code, see SiteEventCode) */
  note: string | null
}

/** Live log line. The UI localises `code`; `url`/`n` are the only parameters. */
export type SiteEventCode =
  | 'start' | 'robots_ok' | 'robots_missing' | 'robots_limited' | 'robots_blocked'
  | 'sitemap_found' | 'pages_picked' | 'page_read' | 'page_failed' | 'page_disallowed' | 'page_thin'
  | 'render_start' | 'render_page' | 'render_skipped' | 'render_failed'
  | 'analyze_start' | 'analyze_retry' | 'done' | 'error'
export interface SiteEvent { at: string; code: SiteEventCode; url?: string; n?: number }

export type Observed = 'observed' | 'inferred'
export interface SiteEvidence { url: string; quote: string }
export interface SiteFeature {
  id: string
  name: string
  summary: string
  group: 'core' | 'supporting' | 'growth' | 'admin' | 'integration'
  basis: Observed
  evidence: SiteEvidence[]
}
export interface SiteFlow { id: string; name: string; actor: string; steps: string[]; basis: Observed }
export interface SitePriceTier { name: string; price: string; period: string; highlights: string[] }
export interface SitePricing {
  model: 'free' | 'freemium' | 'subscription' | 'one_time' | 'usage' | 'contact_sales' | 'marketplace' | 'ads' | 'unknown'
  currency: string | null
  freeTier: boolean | null
  trial: string | null
  tiers: SitePriceTier[]
  notes: string[]
  evidenceUrl: string | null
}
export interface SiteEntity { name: string; fields: string[]; relations: string[] }
export interface SiteTech { name: string; category: TechCategory; evidence: string; confidence: 'high' | 'medium' | 'low' }
export type TechCategory =
  | 'framework' | 'cms' | 'builder' | 'ecommerce' | 'hosting' | 'cdn' | 'analytics' | 'marketing' | 'payments'
  | 'support' | 'auth' | 'monitoring' | 'ui' | 'fonts' | 'maps' | 'video' | 'search' | 'other'
export interface SiteNavItem { label: string; url: string | null; children: string[] }
export interface SiteDesign {
  tone: string
  colors: string[]
  fonts: string[]
  layout: string[]
  notes: string[]
}

/** The dissection report: deterministic parts (tech, nav, design tokens) + the model's reading. */
export interface SiteDissection {
  product: { name: string; oneLiner: string; category: string; audience: string[]; problem: string; valueProps: string[]; businessModel: string }
  features: SiteFeature[]
  flows: SiteFlow[]
  pricing: SitePricing
  entities: SiteEntity[]
  integrations: string[]
  trust: { socialProof: string[]; compliance: string[] }
  design: SiteDesign
  techStack: SiteTech[]
  nav: SiteNavItem[]
  opportunities: string[]
  cautions: string[]
  ideaDraft: string
  interviewTopics: string[]
  limits: string[]
  confidence: 'high' | 'medium' | 'low'
}

/** How the owner relates to the site; decides the interview's first branch and what may be reused. */
export type SiteRelation = 'competitor' | 'inspiration' | 'own' | 'client'
export const SITE_RELATIONS: readonly SiteRelation[] = ['inspiration', 'competitor', 'own', 'client']

export interface SiteAnalysisDto {
  id: string
  planId: string
  url: string
  finalUrl: string | null
  host: string
  notes: string | null
  relation: SiteRelation
  status: SiteStatus
  phase: SitePhase
  pagesTotal: number
  pagesDone: number
  pages: SitePage[]
  events: SiteEvent[]
  result: SiteDissection | null
  /** warning codes: render_unavailable, robots_unreachable, spa_thin, … */
  warnings: string[]
  error: string | null
  errorCode: string | null
  model: string | null
  modelUsed: string | null
  startedAt: string | null
  finishedAt: string | null
  createdAt: string
}

/** Compact marker on the plan payload (GET /api/plan/:id) so the wizard knows the flow. */
export interface PlanSiteRef { id: string; url: string; host: string; status: SiteStatus; phase: SitePhase }

// ------------------------------------------------------------ identity (the /bot page shows these exact strings)
/** plain HTTP reads (robots.txt, pages, sitemaps) */
export const BOT_UA = 'Mozilla/5.0 (compatible; BystepBot/1.0; +https://bystep.dev/bot)'
/** the sandboxed browser (screenshots, JavaScript pages) */
export const BROWSER_UA = 'Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/150.0.0.0 Safari/537.36 BystepBot/1.0 (+https://bystep.dev/bot)'

// ------------------------------------------------------------ limits (shared by server + UI copy)
/** pages read per analysis, the entered page included */
export const SITE_MAX_PAGES = 12
/** pages opened in the sandboxed browser (screenshots + JavaScript pages) */
export const SITE_MAX_RENDERS = 4
/** analyses that reached the AI step, per user per rolling 24 hours */
export const SITE_DAILY_LIMIT: Record<'free' | 'paid' | 'admin', number> = { free: 3, paid: 12, admin: -1 }
export const SITE_NOTES_MAX = 2000
export const SITE_URL_MAX = 2048
